import { Code, ConnectError } from "@connectrpc/connect";
import {
  FundSource,
  HokkaidoR8HouseholderCodeStatus,
} from "schema/gen/es/chiikipoint/model/v2/model_pb";
import {
  type CreateHokkaidoR8HouseholderCodeUseAsWalletBalanceRequest,
  CreateHokkaidoR8HouseholderCodeUseAsWalletBalanceResponse,
  type GetHokkaidoR8HouseholderCodeStatusRequest,
  GetHokkaidoR8HouseholderCodeStatusResponse,
} from "schema/gen/es/chiikipoint/wallet/v2/service_pb";

type MockCode = {
  status: HokkaidoR8HouseholderCodeStatus;
};

// Mock keys mirror the production canonical form: compact 12-char with no
// hyphens. Routes that hit this mock already pass codes in canonical form.
const mockCodes = new Map<string, MockCode>([
  ["222ABCDEFGHJ", { status: HokkaidoR8HouseholderCodeStatus.UNUSED }],
  [
    "222KKKKKKKKK",
    { status: HokkaidoR8HouseholderCodeStatus.USED_AS_WALLET_BALANCE },
  ],
  [
    "222VVVVVVVVV",
    { status: HokkaidoR8HouseholderCodeStatus.USED_AS_PAPER_VOUCHER },
  ],
]);

// Reserved code that always throws Code.Internal, used to verify the
// ErrorBoundary path (ErrorPage + Sentry).
const INTERNAL_ERROR_CODE = "222INTERNAL0";

const hokkaidoFundSource = new FundSource({
  id: "hokkaido-householder",
  name: "道民生活応援ポイント",
  priorityWeight: BigInt(1),
  forbidTransfer: true,
});

const hokkaidoAmount = BigInt(5_500);

export async function handleGetHokkaidoR8HouseholderCodeStatus(
  req: GetHokkaidoR8HouseholderCodeStatusRequest,
) {
  await new Promise((r) => setTimeout(r, 200));
  if (req.code === INTERNAL_ERROR_CODE) {
    throw new ConnectError("simulated internal error", Code.Internal);
  }
  const found = mockCodes.get(req.code);
  if (!found) {
    // Spec: PermissionDenied to avoid code enumeration.
    throw new ConnectError("not usable", Code.PermissionDenied);
  }
  return new GetHokkaidoR8HouseholderCodeStatusResponse({
    status: found.status,
    amount: hokkaidoAmount,
    fundSource: hokkaidoFundSource,
  });
}

export async function handleCreateHokkaidoR8HouseholderCodeUseAsWalletBalance(
  req: CreateHokkaidoR8HouseholderCodeUseAsWalletBalanceRequest,
) {
  await new Promise((r) => setTimeout(r, 400));
  if (req.code === INTERNAL_ERROR_CODE) {
    throw new ConnectError("simulated internal error", Code.Internal);
  }
  const found = mockCodes.get(req.code);
  if (!found) {
    throw new ConnectError("not usable", Code.PermissionDenied);
  }
  if (found.status === HokkaidoR8HouseholderCodeStatus.USED_AS_PAPER_VOUCHER) {
    throw new ConnectError(
      "code already used as paper voucher",
      Code.FailedPrecondition,
    );
  }
  if (found.status === HokkaidoR8HouseholderCodeStatus.USED_AS_WALLET_BALANCE) {
    throw new ConnectError(
      "code already used as wallet balance",
      Code.FailedPrecondition,
    );
  }
  found.status = HokkaidoR8HouseholderCodeStatus.USED_AS_WALLET_BALANCE;
  return new CreateHokkaidoR8HouseholderCodeUseAsWalletBalanceResponse({
    amount: hokkaidoAmount,
    fundSource: hokkaidoFundSource,
  });
}
